import { useCustomersContext } from '@src/hooks/useCustomer';
import {
  CustomerAddressStatusEnum,
  getRowNodesToOperateOn,
  IconName,
  useDynamicCallback,
  type AgGridMenuItems,
  type EntityAdminClass,
  type ICustomerAddress,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams } from 'ag-grid-community';
import { compact } from 'lodash-es';

export const useConfirmCustomerAddressMenu = ({
  isAuthorizedToConfirm,
}: {
  isAuthorizedToConfirm: boolean;
}): {
  getCustomContextMenuItems: (
    params: GetContextMenuItemsParams<EntityAdminClass<ICustomerAddress>>
  ) => AgGridMenuItems<EntityAdminClass<ICustomerAddress>>;
} => {
  const { confirmCustomerAddress } = useCustomersContext();

  const getCustomContextMenuItems = useDynamicCallback(
    (params: GetContextMenuItemsParams<EntityAdminClass<ICustomerAddress>>) => {
      if (!isAuthorizedToConfirm) {
        return [];
      }

      const clickedStatus = params.node?.data?.data?.Status;
      if (clickedStatus !== CustomerAddressStatusEnum.Pending) {
        return [];
      }

      const selectedNodes = getRowNodesToOperateOn(params);
      const nodesToOperateOn = selectedNodes.length > 0 ? selectedNodes : compact([params.node]);
      const addressesToConfirm = compact(nodesToOperateOn.map(node => node.data?.data)).filter(
        address => address.Status === clickedStatus
      );

      if (addressesToConfirm.length === 0) {
        return [];
      }

      return [
        {
          name: `Confirm (${addressesToConfirm.length})`,
          action: () => {
            addressesToConfirm.forEach(address => {
              confirmCustomerAddress(address.CustomerAddressID);
            });
          },
          icon: `<i class="ag-icon ${IconName.CheckCircleSolid}"/>`,
        },
      ];
    }
  );

  return {
    getCustomContextMenuItems,
  };
};
